/**
 * Signup Page Component
 * Handles user registration with email, password, and role selection
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config/apiConfig';
import './Auth.css';
import BrandLogo from '../assets/images/big-atom-logo copy.png';

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    orgName: '',
    orgType: 'INDIVIDUAL',
    role: 'INDIVIDUAL',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const orgTypeOptions = [
    { value: 'INDIVIDUAL', label: 'Individual' },
    { value: 'AGENCY', label: 'Agency' },
  ];

  const roleOptions = [
    { value: 'AGENCY', label: 'Agency User' },
    { value: 'INDIVIDUAL', label: 'Individual User' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === 'orgType') {
        updated.role = value === 'AGENCY' ? 'AGENCY' : 'INDIVIDUAL';
      }
      return updated;
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validatePassword = (password) => {
    // Minimum 8 characters, at least one letter and one number
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/[a-zA-Z]/.test(password)) {
      return 'Password must contain at least one letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate password
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      console.log('📝 Attempting signup:', {
        email: formData.email,
        role: formData.role,
        orgType: formData.orgType,
        orgName: formData.orgName,
      });

      const response = await apiService.post(API_ENDPOINTS.SIGNUP, {
        email: formData.email,
        password: formData.password,
        orgName: formData.orgName,
        orgType: formData.orgType,
        role: formData.role,
      });

      console.log('✅ Signup successful:', response);

      setSuccess(true);

      if (response.token || response.accessToken) {
        localStorage.setItem('auth_token', response.token || response.accessToken);
      }

      const resolvedOrgNameRaw =
        response.orgName || response.organizationName || formData.orgName || '';
      const resolvedOrgName =
        typeof resolvedOrgNameRaw === 'string' ? resolvedOrgNameRaw.trim() : '';

      try {
        localStorage.setItem(
          'user_profile',
          JSON.stringify({
            email: formData.email,
            role: formData.role,
            orgId: response.orgId ?? null,
            orgName: resolvedOrgName,
          })
        );
      } catch (storageError) {
        console.warn('Unable to persist user profile to storage after signup:', storageError);
      }

      setTimeout(() => {
        navigate('/welcome', { replace: true });
      }, 1200);
    } catch (err) {
      console.error('❌ Signup error:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to create account. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-layout">
        <aside className="auth-brand">
          <div className="auth-brand-content">
            <img src={BrandLogo} alt="Big Atom" className="auth-brand-logo" />
            <h1 className="auth-brand-title">Welcome to Big Atom</h1>
            <p className="auth-brand-subtitle">
              You’re moments away from richer signal and a clearer view of your Conversions API health.
            </p>
            <ul className="auth-usp-list">
              <li>Unified overview across every pixel and dataset</li>
              <li>Inline diagnostics with actionable next steps</li>
              <li>Alerts your revenue team can trust</li>
            </ul>
          </div>
        </aside>
        <main className="auth-panel">
          <div className="auth-panel-card auth-panel-card--status">
            <div className="auth-status-icon">✓</div>
            <h2 className="auth-panel-title">Account created</h2>
            <p className="auth-panel-subtitle">Preparing your workspace…</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <aside className="auth-brand">
        <div className="auth-brand-content">
          <img src={BrandLogo} alt="Big Atom" className="auth-brand-logo" />
          <h1 className="auth-brand-title">Create your Big Atom workspace</h1>
          <p className="auth-brand-subtitle">
            Bring web and offline conversions into a single source of truth with proactive quality monitoring.
          </p>
          <ul className="auth-usp-list">
            <li>Unified metrics across every pixel and dataset</li>
            <li>Early warning signals for EMQ, ACR, and coverage drops</li>
            <li>Collaboration-ready dashboards for growth teams</li>
          </ul>
        </div>
      </aside>

      <main className="auth-panel">
        <div className="auth-panel-card">
          <div className="auth-panel-header">
            <h2 className="auth-panel-title">Create account</h2>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && (
              <div className="auth-error">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Create a secure password"
                required
                autoComplete="new-password"
                minLength={6}
              />
              <p className="form-hint">At least 6 characters including letters and numbers</p>
            </div>

            <div className="form-group">
              <label htmlFor="orgName" className="form-label">
                Organization Name
              </label>
              <input
                type="text"
                id="orgName"
                name="orgName"
                value={formData.orgName}
                onChange={handleChange}
                className="form-input"
                placeholder="Your company or brand"
                required
                autoComplete="organization"
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="orgType" className="form-label">
                  Organization Type
                </label>
                <select
                  id="orgType"
                  name="orgType"
                  value={formData.orgType}
                  onChange={handleChange}
                  className="form-input form-select"
                  required
                >
                  {orgTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="role" className="form-label">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="form-input form-select"
                  required
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="auth-button auth-button-primary"
              disabled={loading}
            >
              {loading ? 'Creating your workspace…' : 'Create Account'}
            </button>
          </form>

          <div className="auth-panel-footer">
            <span>Already have an account?</span>{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Signup;

